import { motion } from "framer-motion";
import { Instagram, Heart, Play, Star, Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/animations/AnimatedSection";

// Import real UGC images - these are deferred loaded
import ugcIns1 from "@/assets/ugc/ugc-ins-1.png";
import ugcIns2 from "@/assets/ugc/ugc-ins-2.png";
import ugcIns3 from "@/assets/ugc/ugc-ins-3.png";
import ugcIns4 from "@/assets/ugc/ugc-ins-4.png";
import ugcIns5 from "@/assets/ugc/ugc-ins-5.png";
import ugcIns6 from "@/assets/ugc/ugc-ins-6.png";

interface UGCPost {
  id: number;
  type: "image" | "video";
  imageUrl: string;
  username: string;
  caption: string;
  likes: number;
  rating?: number;
}

// Real UGC data with imported images
const ugcPosts: UGCPost[] = [
  {
    id: 1,
    type: "image",
    imageUrl: ugcIns1,
    username: "Verified Customer",
    caption: "My copper bracelet goes everywhere with me! Perfect for my morning coffee ritual ✨",
    likes: 234,
    rating: 5,
  },
  {
    id: 2,
    type: "image",
    imageUrl: ugcIns2,
    username: "Verified Customer",
    caption: "Loving my amethyst bracelet! Adds calm to my workday 💜",
    likes: 189,
    rating: 4.5,
  },
  {
    id: 3,
    type: "image",
    imageUrl: ugcIns3,
    username: "Verified Customer",
    caption: "Perfect accessory for relaxation time. Love the hammered copper design 🧘‍♀️",
    likes: 312,
    rating: 5,
  },
  {
    id: 4,
    type: "image",
    imageUrl: ugcIns4,
    username: "Verified Customer",
    caption: "My black bracelet is so elegant! Goes with everything 🖤",
    likes: 156,
    rating: 4.5,
  },
  {
    id: 5,
    type: "image",
    imageUrl: ugcIns5,
    username: "Verified Customer",
    caption: "My new Calmi ring! Perfect companion for morning coffee 💎",
    likes: 278,
    rating: 5,
  },
  {
    id: 6,
    type: "image",
    imageUrl: ugcIns6,
    username: "Verified Customer",
    caption: "The complete set - ring and bracelet. Perfect for everyday balance ☕",
    likes: 198,
    rating: 4.5,
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export const UGCSection = () => {
  return (
    <AnimatedSection className="section-padding bg-secondary/20">
      <div className="container-calim">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center mb-8 md:mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full mb-4">
            <Instagram className="h-4 w-4 text-pink-600" />
            <span className="text-sm font-medium bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              @calmistyle
            </span>
          </div>
          <h2 className="text-headline mb-4">Our Community in Action</h2>
          <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Join over 2,500 customers sharing their wellness experience. 
            Tag us to be featured here!
          </p>
        </motion.div>

        {/* UGC Grid - Images with native lazy loading */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3 mb-8 md:mb-10">
          {ugcPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeInUp}
              transition={{ delay: index * 0.05 }}
              className="group relative aspect-square rounded-lg md:rounded-xl overflow-hidden cursor-pointer bg-muted"
            >
              <img
                src={post.imageUrl}
                alt={`Photo by ${post.username}`}
                loading="lazy"
                decoding="async"
                width={300}
                height={300}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              
              {/* Video indicator */}
              {post.type === "video" && (
                <div className="absolute top-2 right-2 w-6 md:w-8 h-6 md:h-8 bg-black/60 rounded-full flex items-center justify-center">
                  <Play className="h-3 md:h-4 w-3 md:w-4 text-white fill-white" />
                </div>
              )}

              {/* Rating badge */}
              {post.rating && (
                <div className="absolute top-2 left-2 flex items-center gap-0.5 bg-black/60 rounded-full px-1.5 md:px-2 py-0.5 md:py-1">
                  {[...Array(Math.floor(post.rating))].map((_, i) => (
                    <Star key={i} className="h-2 md:h-2.5 w-2 md:w-2.5 text-yellow-400 fill-yellow-400" />
                  ))}
                  {post.rating % 1 !== 0 && (
                    <Star className="h-2 md:h-2.5 w-2 md:w-2.5 text-yellow-400 fill-yellow-400/50" />
                  )}
                </div>
              )}

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-2 md:p-3">
                <div className="flex items-center gap-2 text-white">
                  <div className="flex items-center gap-1">
                    <Heart className="h-3 w-3 fill-white" />
                    <span className="text-xs">{post.likes}</span>
                  </div>
                </div>
                <p className="text-white/80 text-xs mt-1 line-clamp-2 hidden sm:block">{post.caption}</p>
                <span className="text-white/60 text-xs mt-1">{post.username}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Testimonial Highlight */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="bg-background rounded-xl md:rounded-2xl p-5 md:p-8 relative overflow-hidden max-w-3xl mx-auto mb-8 md:mb-10"
        >
          <Quote className="absolute top-4 left-4 h-12 md:h-16 w-12 md:w-16 text-primary/5" />
          <div className="relative z-10 text-center">
            <p className="text-base md:text-xl italic text-foreground mb-4 md:mb-6 leading-relaxed">
              "After 2 weeks of use, my stress management has completely changed. 
              The rotational motion has become a soothing reflex I use in meetings, 
              during commutes... everywhere!"
            </p>
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 md:w-12 h-10 md:h-12 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center">
                <span className="font-semibold text-primary text-sm md:text-base">SL</span>
              </div>
              <div className="text-left">
                <p className="font-medium text-sm md:text-base">Sophie L.</p>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                  ))}
                  <span className="text-xs text-muted-foreground ml-1">Verified Purchase</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4"
        >
          <a
            href="https://instagram.com/calmistyle"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-medium hover:shadow-lg transition-all hover:scale-105 text-sm md:text-base"
          >
            <Instagram className="h-4 md:h-5 w-4 md:w-5" />
            Follow Us on Instagram
          </a>
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 border border-border rounded-full font-medium hover:bg-secondary transition-colors text-sm md:text-base"
          >
            See All Reviews
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};
